import { Image as GalleryImage } from '@anwb/poncho/components/gallery';
import { VehicleMediaData } from 'types';

import { getAvailableImages } from './getAvailableImages';

/**
 * Returns an array of Image objects to be used to display the images in a Poncho Gallery, based on the
 * VehicleMediaData array that is present in the occasion data from the partner.
 *
 * VehicleMediaData as returned from the partner is processed in 3 steps:
 * 1. The data is filtered based on some business logic, with different filtering steps for interior and exterior images of the car
 * (see filtering methods filterInteriorImages and filterExteriorImages)
 * 2. The data is checked for availability, to prevent passing unavailable images to the Gallery component.
 * 3. The data is mapped to a GalleryImage object fit for passing to the Poncho Gallery.
 *
 *
 * @param {VehicleMediaData[]} vehicleMediaData - An array of VehicleMediaData objects containing the images.
 * @param {string} carName - The name of the car.
 * @returns {GalleryImage[]} An array of Image objects to be used in the Poncho Gallery.
 */
export const getOccasionImages = async (
	vehicleMediaData: VehicleMediaData[],
	carName: string
): Promise<GalleryImage[]> => {
	if (!vehicleMediaData) {
		return [];
	}
	const filteredExteriorImages = filterExteriorImages(vehicleMediaData);
	const filteredInteriorImages = filterInteriorImages(vehicleMediaData);

	const availableExteriorImages = await getAvailableImages(filteredExteriorImages);

	const availableInteriorImages = await getAvailableImages(filteredInteriorImages);

	const mappedInteriorImages = mapVehicleMediaDataToImage(
		availableInteriorImages,
		`Afbeelding van het interieur van ${carName}`
	);
	const mappedExteriorImages = mapVehicleMediaDataToImage(
		availableExteriorImages,
		`Afbeelding van het exterieur van ${carName}`
	);

	return [...mappedExteriorImages, ...mappedInteriorImages];
};

export const mapVehicleMediaDataToImage = (
	vehicleMediaData: VehicleMediaData[],
	altText: string
): GalleryImage[] =>
	vehicleMediaData.map((img) => ({
		type: 'image',
		caption: '', // No caption as this will be shown below the image gallery
		alt: altText,
		src: img.url,
	}));

/*
 * VehicleMediaData of type 'IMAGE' are photos of the interior of the car.
 *
 * The photos of type 'IMAGE' are filtered based on two criteria:
 * - They occasionally contain a photo of the exterior as well. These are filted out based on checking the presence of '/360doorsclosed/' in the photo-url.
 * - They also contain a photo with 'isDefault: true' property - this is also filtered out, because this is photo is also present in the 'IMAGE_360' photos.
 */
export const filterInteriorImages = (vehicleMediaData: VehicleMediaData[]): VehicleMediaData[] =>
	vehicleMediaData.filter(
		(media) =>
			media.type === 'IMAGE' &&
			// We have to omit the default IMAGE as the url of the default image is already used as one of the other
			// images, to prevent duplicate images in the gallery.
			!media.isDefault &&
			// Some images returned are still 360 images, we can use the path to exclude these from showing
			!media.url.includes('/360doorsclosed/')
	);

/*
 * The VehicleMediaData array contains 'IMAGE_360' type images, which are photos taken of the exterior of the car,
 *
 * To limit the number of exterior images being shown in the gallery, only every 4th exterior image is retained, and
 * the others are filtered out.
 */
export const filterExteriorImages = (vehicleMediaData: VehicleMediaData[]): VehicleMediaData[] => {
	let exterior360Images = vehicleMediaData.filter((media) => media.type === 'IMAGE_360');

	if (exterior360Images.length > 4) {
		exterior360Images = exterior360Images.filter((_, index) => index % 4 === 0);
	}

	return exterior360Images;
};
